import { useEffect, useState } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";

type SubmittedTestRow = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  startTime?: string | null;
  submitTime?: string | null;
  scorePullTime?: string | null;
  reportRecipient?: string | null;
  department?: string | null;
  jobTitle?: string | null;
  directSupervisor?: string | null;
};

type SubmittedTestsReportModalProps = {
  isOpen: boolean;
  companyId?: number;
  onClose: () => void;
  updateSpinner?: (show: boolean) => void;
};

function toInputDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultStartDate(): string {
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return toInputDate(start);
}

function defaultEndDate(): string {
  return toInputDate(new Date());
}

function csvEscape(value: string | null | undefined): string {
  const text = value ?? "";
  if (/[",\n\r]/.test(text))
    return `"${text.replace(/"/g, "\"\"")}"`;
  return text;
}

function rowsToCsv(rows: SubmittedTestRow[]): string {
  const header = [
    "Id",
    "FirstName",
    "LastName",
    "StartTime",
    "SubmitTime",
    "ScorePullTime",
    "ReportRecipient",
    "Department",
    "Your Job Title",
    "Your Direct Supervisor",
  ];
  const lines = [header.join(",")];
  for (const row of rows) {
    lines.push([
      String(row.id),
      csvEscape(row.firstName),
      csvEscape(row.lastName),
      csvEscape(row.startTime),
      csvEscape(row.submitTime),
      csvEscape(row.scorePullTime),
      csvEscape(row.reportRecipient),
      csvEscape(row.department),
      csvEscape(row.jobTitle),
      csvEscape(row.directSupervisor),
    ].join(","));
  }
  return lines.join("\r\n");
}

function downloadCsvFile(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function SubmittedTestsReportModal({
  isOpen,
  companyId,
  onClose,
  updateSpinner,
}: SubmittedTestsReportModalProps) {
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStartDate(defaultStartDate());
      setEndDate(defaultEndDate());
    }
  }, [isOpen]);

  async function downloadAsync() {
    if (!companyId) {
      toast.error("Company information not available.");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please provide a start and end date.");
      return;
    }
    if (endDate < startDate) {
      toast.error("End date must be on or after start date.");
      return;
    }

    updateSpinner?.(true);
    setDownloading(true);
    try {
      const url = `/Company/${companyId}/SubmittedTestsReport?startDate=${startDate}&endDate=${endDate}`;
      const resp = await RightTimeApi.getAsync(url);
      if (!resp?.ok || !resp.jsonData) {
        toast.error("Failed to generate report.");
        return;
      }
      const rows: SubmittedTestRow[] = Array.isArray(resp.jsonData.rows) ? resp.jsonData.rows : [];
      if (rows.length === 0) {
        toast.info("No submitted tests in this date range.");
        return;
      }
      downloadCsvFile(rowsToCsv(rows), `SubmittedTests-${companyId}-${startDate}-${endDate}.csv`);
      toast.success("Report downloaded.");
      onClose();
    } catch {
      toast.error("Error generating report.");
    } finally {
      setDownloading(false);
      updateSpinner?.(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Submitted Tests"
      size="md"
      className="submittedTestsReportModal"
      footer={(
        <div className="footerRight">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={downloadAsync} disabled={downloading}>
            Download
          </button>
        </div>
      )}
    >
      <div className="submittedTestsReportBody">
        <label className="fieldRow">
          <span className="fieldLabel">Start date</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="fieldRow">
          <span className="fieldLabel">End date</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>
    </Modal>
  );
}
